// FASE 3.1 — Analyst monetization dashboard (/shadow/monetiza). PRO only.
// Connect onboarding + price setter + subscriber list + F5.5 co-analyst split.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Crown, Loader2, ExternalLink, DollarSign, Users, Check, AlertCircle, Handshake, X as XIcon, Coins, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";
import { startProCheckout } from "../components/shadow/ProGate";

export default function ShadowMonetizaPage() {
  const { user, token, openLogin, isPro } = useAuth();
  const { lang } = useLanguage();
  const [status, setStatus] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [err, setErr] = useState(null);

  // F5.5 — co-analyst split state
  const [coUsername, setCoUsername] = useState("");
  const [coPct, setCoPct] = useState(50);
  const [coBusy, setCoBusy] = useState(false);
  const [coErr, setCoErr] = useState(null);
  const [transfers, setTransfers] = useState({ sent: [], received: [] });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const loadAll = useCallback(async () => {
    if (!user || !isPro) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [s, sub, tr] = await Promise.all([
        fetch(`${API}/shadow/connect/status`, { credentials: "include", headers }).then(safeJson),
        fetch(`${API}/shadow/connect/subscribers`, { credentials: "include", headers }).then(safeJson),
        fetch(`${API}/shadow/connect/co-analyst/transfers`, { credentials: "include", headers }).then(safeJson),
      ]);
      if (s) {
        setStatus(s);
        if (s.subscription_price_eur) setPrice(String(s.subscription_price_eur));
        if (s.co_analyst_username) setCoUsername(s.co_analyst_username);
        if (s.co_analyst_split_pct) setCoPct(s.co_analyst_split_pct);
      }
      if (sub?.subscribers) setSubscribers(sub.subscribers);
      if (tr) setTransfers({ sent: tr.sent || [], received: tr.received || [] });
    } finally { setLoading(false); }
  }, [user, token, isPro]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // F5.5 — Save/remove co-analyst
  const saveCoAnalyst = useCallback(async () => {
    const pct = parseInt(coPct, 10);
    const uname = coUsername.trim().replace(/^@/, "");
    if (!uname) { setCoErr(tFases(lang, "ca_err_404")); return; }
    if (!Number.isFinite(pct) || pct < 1 || pct > 99) { setCoErr(tFases(lang, "ca_err_invalid_pct")); return; }
    setCoBusy(true); setCoErr(null);
    try {
      const res = await fetch(`${API}/shadow/connect/co-analyst`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ username: uname, split_pct: pct }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        // Server returns {detail:{error:"..."}} for business errors.
        // Be defensive — safeJson may return null if the body was consumed by
        // a fetch interceptor (PostHog etc.); fall back to res.status mapping.
        let code = (data && data.detail && data.detail.error) || (typeof data?.detail === "string" ? data.detail : null);
        const map = {
          user_not_found: "ca_err_404",
          self_not_allowed: "ca_err_self",
          co_analyst_must_be_pro: "ca_err_pro",
          co_analyst_not_onboarded: "ca_err_onboard",
        };
        // Status-based fallback when body parsing failed.
        if (!map[code]) {
          if (res.status === 404) code = "user_not_found";
          else if (res.status === 422) code = null; // pydantic validation
        }
        if (!data) {
          console.warn("coanalyst save error: body unparseable, status=", res.status);
        }
        const msg = map[code]
          ? tFases(lang, map[code])
          : (data?.detail?.message || (typeof data?.detail === "string" ? data.detail : null) || tFases(lang, "ca_err_invalid_pct"));
        throw new Error(msg);
      }
      setStatus((s) => ({ ...(s || {}), co_analyst_username: data.co_analyst_username, co_analyst_split_pct: data.co_analyst_split_pct }));
    } catch (e) { setCoErr(e.message); }
    finally { setCoBusy(false); }
  }, [coUsername, coPct, lang, token]);

  const removeCoAnalyst = useCallback(async () => {
    setCoBusy(true); setCoErr(null);
    try {
      await fetch(`${API}/shadow/connect/co-analyst`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setStatus((s) => ({ ...(s || {}), co_analyst_username: null, co_analyst_split_pct: null }));
      setCoUsername(""); setCoPct(50);
    } finally { setCoBusy(false); }
  }, [token]);

  const startOnboarding = useCallback(async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/shadow/connect/onboard`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "x-frontend-origin": window.location.origin, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.detail?.message || data?.detail || tFases(lang, "mz_onboard_err"));
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      setErr(e.message); setBusy(false);
    }
  }, [busy, token]);

  const savePrice = useCallback(async () => {
    const p = parseFloat(price);
    if (!isFinite(p) || p < 1 || p > 200) { setErr(tFases(lang, "mz_price_invalid")); return; }
    setErr(null);
    try {
      const res = await fetch(`${API}/shadow/connect/price`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ price_eur: p }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.detail || tFases(lang, "mz_price_save_err"));
      setStatus((s) => ({ ...(s || {}), subscription_price_eur: data.price_eur }));
    } catch (e) { setErr(e.message); }
  }, [price, token]);

  if (!user) return <Gate title={tFases(lang, "mz_title")} subtitle={tFases(lang, "mz_login_sub")} cta={tFases(lang, "common_login")} onClick={openLogin} />;
  if (!isPro) return <Gate title={tFases(lang, "mz_title")} subtitle={tFases(lang, "mz_pro_sub")} cta={tFases(lang, "j_pro_cta")} onClick={() => { startProCheckout().catch(() => {}); }} />;

  const ready = status?.payouts_enabled && status?.charges_enabled;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="monetiza-page">
      <Navbar />
      <section className="mx-auto max-w-4xl px-6 pt-32 pb-24">
        <Link to="/shadow" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-amber-300" data-testid="monetiza-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>
        <div className="mt-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">{tFases(lang, "mz_eyebrow")}</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">{tFases(lang, "mz_title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">
            {tFases(lang, "mz_h1_sub")}
          </p>
        </div>

        {/* R₿L Subscriptions hub link — payment rail parallel to Stripe */}
        <Link
          to="/shadow/subscripciones-rbl"
          data-testid="monetiza-rbl-link"
          className="group mt-6 flex items-center justify-between gap-4 rounded-2xl border border-amber-400/30 bg-gradient-to-r from-amber-500/[0.08] via-orange-500/[0.04] to-transparent p-5 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-amber-400/60 hover:shadow-[0_8px_28px_rgba(251,191,36,0.18)]"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-amber-400/15 p-2.5 ring-1 ring-amber-400/30">
              <Coins className="h-5 w-5 text-amber-300" />
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-200/80">
                {{ es: "// PAGOS EN R₿L", en: "// R₿L PAYMENTS", sk: "// PLATBY R₿L", de: "// ZAHLUNGEN IN R₿L", "zh-CN": "// R₿L 支付" }[lang] || "// PAGOS EN R₿L"}
              </p>
              <p className="mt-1 text-base font-bold text-white">
                {{
                  es: "Tus suscripciones R₿L",
                  en: "Your R₿L subscriptions",
                  sk: "Tvoje predplatné R₿L", de: "Deine R₿L-Abonnements", "zh-CN": "您的 R₿L 订阅",
                }[lang] || "Tus suscripciones R₿L"}
              </p>
              <p className="mt-0.5 text-xs text-white/55">
                {{
                  es: "Gestiona suscriptores R₿L, ingresos del mes, historial de cobros y cancelaciones.",
                  en: "Manage R₿L subscribers, monthly income, billing history and cancellations.",
                  sk: "Spravuj predplatiteľov R₿L, mesačné príjmy, históriu platieb a zrušenia.", de: "Verwalten Sie R₿L-Abonnenten, monatliche Einnahmen, Zahlungshistorie und Kündigungen.", "zh-CN": "管理 R₿L 订阅者、本月收入、付款历史和取消记录。",
                }[lang] || ""}
              </p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 text-amber-300 transition-transform group-hover:translate-x-1" />
        </Link>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> {tFases(lang, "common_loading")}</div>
        ) : (
          <div className="mt-8 space-y-4">
            {/* Connect status */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-white/75">
                <Crown className="h-4 w-4 text-amber-300" /> {tFases(lang, "mz_connect_title")}
              </h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label={tFases(lang, "mz_stat_account_id")} value={status?.stripe_account_id ? status.stripe_account_id.slice(0, 12) + "…" : "—"} />
                <Stat label={tFases(lang, "mz_stat_onboarded")} value={status?.details_submitted ? tFases(lang, "mz_yes") : tFases(lang, "mz_no")} good={status?.details_submitted} />
                <Stat label={tFases(lang, "mz_stat_charges")} value={status?.charges_enabled ? tFases(lang, "mz_active") : "—"} good={status?.charges_enabled} />
                <Stat label={tFases(lang, "mz_stat_payouts")} value={status?.payouts_enabled ? tFases(lang, "mz_ready") : "—"} good={status?.payouts_enabled} />
              </div>
              <button onClick={startOnboarding} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-black disabled:opacity-60" data-testid="monetiza-onboard-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                {status?.details_submitted ? tFases(lang, "mz_onboard_update") : tFases(lang, "mz_onboard_btn")}
              </button>
            </div>

            {/* Price */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-white/75">
                <DollarSign className="h-4 w-4 text-emerald-300" /> {tFases(lang, "mz_price_title")}
              </h2>
              <p className="mt-1 text-xs text-white/55">{tFases(lang, "mz_price_sub")}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-white/40 text-sm">€</span>
                <input type="number" min="1" max="200" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="9.99" className="w-32 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-emerald-400/50" data-testid="monetiza-price-input" />
                <span className="text-white/40 text-sm">{tFases(lang, "mz_price_per_month")}</span>
                <button onClick={savePrice} className="ml-3 inline-flex items-center gap-1 rounded-full bg-emerald-400 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-black" data-testid="monetiza-price-save">
                  {tFases(lang, "common_save")}
                </button>
              </div>
            </div>

            {/* Subscribers list */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-white/75">
                <Users className="h-4 w-4 text-cyan-300" /> {tFases(lang, "mz_subs_title")} ({subscribers.length})
              </h2>
              {subscribers.length === 0 ? (
                <p className="mt-2 text-xs text-white/45">{ready ? tFases(lang, "mz_subs_empty_share") : tFases(lang, "mz_subs_empty_onboard")}</p>
              ) : (
                <ul className="mt-3 divide-y divide-white/5">
                  {subscribers.map((s) => (
                    <li key={s.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`monetiza-sub-${s.id}`}>
                      <span className="font-mono text-xs text-white/55">{s.subscriber_id.slice(0, 12)}…</span>
                      <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-emerald-200">
                        {s.status} · €{s.price_eur}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* F5.5 — Co-analyst split */}
            <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.04] p-5 backdrop-blur" data-testid="monetiza-coanalyst-card">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-cyan-200">
                <Handshake className="h-4 w-4" /> {tFases(lang, "ca_title")}
              </h2>
              <p className="mt-1 text-xs text-white/55">{tFases(lang, "ca_sub")}</p>

              {status?.co_analyst_username && (
                <p className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3 py-2 text-xs text-emerald-200" data-testid="monetiza-coanalyst-current">
                  <Check className="inline h-3 w-3 mr-1" />
                  {tFases(lang, "ca_current", { name: status.co_analyst_username, pct: status.co_analyst_split_pct })}
                </p>
              )}

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_140px_auto]">
                <input
                  value={coUsername}
                  onChange={(e) => setCoUsername(e.target.value.slice(0, 21))}
                  placeholder={tFases(lang, "ca_username_ph")}
                  maxLength={21}
                  data-testid="monetiza-coanalyst-username"
                  className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="1" max="99" step="1" value={coPct}
                    onChange={(e) => setCoPct(e.target.value)}
                    data-testid="monetiza-coanalyst-pct"
                    className="w-20 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-cyan-400/50"
                  />
                  <span className="text-[11px] text-white/55">{tFases(lang, "ca_pct_label")}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveCoAnalyst} disabled={coBusy} data-testid="monetiza-coanalyst-save"
                    className="inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-black disabled:opacity-50">
                    {coBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    {tFases(lang, "ca_save")}
                  </button>
                  {status?.co_analyst_username && (
                    <button onClick={removeCoAnalyst} disabled={coBusy} data-testid="monetiza-coanalyst-remove"
                      className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-400/10 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-rose-200 hover:bg-rose-400/20 disabled:opacity-50">
                      <XIcon className="h-3 w-3" /> {tFases(lang, "ca_remove")}
                    </button>
                  )}
                </div>
              </div>
              {coErr && (
                <p className="mt-2 flex items-center gap-1 text-[11px] text-rose-300" data-testid="monetiza-coanalyst-err">
                  <AlertCircle className="h-3 w-3" /> {coErr}
                </p>
              )}

              {/* Recent transfers */}
              <div className="mt-5">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">
                  {tFases(lang, "ca_transfers_title")} ({transfers.sent.length + transfers.received.length})
                </p>
                {transfers.sent.length + transfers.received.length === 0 ? (
                  <p className="mt-2 text-[11px] text-white/45">{tFases(lang, "ca_transfers_empty")}</p>
                ) : (
                  <ul className="mt-2 divide-y divide-white/5 rounded-lg border border-white/10 bg-black/30">
                    {[
                      ...transfers.sent.map((t) => ({ ...t, _dir: "sent" })),
                      ...transfers.received.map((t) => ({ ...t, _dir: "received" })),
                    ]
                      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                      .slice(0, 10)
                      .map((t) => (
                        <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`monetiza-transfer-${t.id}`}>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${
                            t._dir === "sent" ? "border border-amber-400/40 bg-amber-400/10 text-amber-200" : "border border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                          }`}>
                            {t._dir === "sent" ? tFases(lang, "ca_transfer_sent") : tFases(lang, "ca_transfer_received")}
                          </span>
                          <span className="font-mono text-white/80">
                            €{((t.amount || 0) / 100).toFixed(2)} · {t.split_pct}%
                          </span>
                          <span className="font-mono text-[10px] text-white/40">
                            {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}
                          </span>
                          <span className={`text-[10px] ${
                            t.status === "succeeded" ? "text-emerald-300" : t.status === "failed" ? "text-rose-300" : "text-white/40"
                          }`}>
                            {t.status === "failed" ? tFases(lang, "ca_transfer_failed") : t.status === "pending" ? tFases(lang, "ca_transfer_pending") : "✓"}
                          </span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>

            {err && (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/[0.08] p-3 text-xs text-rose-200" data-testid="monetiza-err">
                <AlertCircle className="inline h-3.5 w-3.5 mr-1" /> {err}
              </div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value, good }) => (
  <div className={`rounded-xl border p-3 ${good ? "border-emerald-400/30 bg-emerald-400/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
    <p className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/45">{label}</p>
    <p className={`mt-1 font-mono text-xs font-bold ${good ? "text-emerald-200" : "text-white/65"}`}>
      {good ? <Check className="inline h-3 w-3 mr-1" /> : null}
      {value}
    </p>
  </div>
);

const Gate = ({ title, subtitle, cta, onClick, href }) => (
  <div className="min-h-screen bg-black text-white">
    <Navbar />
    <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
      <Crown className="mx-auto h-10 w-10 text-amber-300" />
      <h1 className="mt-4 text-3xl font-black tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-white/55">{subtitle}</p>
      {href ? (
        <Link to={href} className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black">{cta}</Link>
      ) : (
        <button onClick={onClick} data-testid="monetiza-gate-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(255,140,0,0.55)]">{cta}</button>
      )}
    </section>
    <Footer />
  </div>
);
